/**
 * Store: Multi-store
 * -----------------------------------------------------------------------------
 * Handles localisation events and data.
 *
 */
export default {
  namespaced: true,

  state: () => ({
    allLanguages: [],
    continents: {},
    current: {
      country: {},
      langauge: {},
    },
    geolocation: {
      countryCode: '',
      name: '',
    },
    preference: false,
    selectedStore: {},
    suggestedStore: {
      country: {},
      language: {},
    },
    supported: {},
    trigger: 'options',
  }),

  mutations: {

    /**
     * Set array for all language strings.
     * @param {Array} languages - Translated language strings.
     */
    SET_ALL_LANGUAGES(state, languages) {
      state.allLanguages = languages
    },

    /**
     * Set object for continents organisation.
     * @param {Array} continents - Continents organisation from section
     * settings.
     */
    SET_CONTINENTS(state, continents) {
      state.continents = continents
    },

    /**
     * Set current country and language.
     * @param {Object} current - Current Shopify localization country and
     * language.
     */
    SET_CURRENT(state, current) {
      state.current = current
    },

    /**
     * Set geolocation.
     * @param {Object} geolocation - Formatted geolocation.
     * @param {String} geolocation.country - Country ISO code.
     * @param {String} geolocation.name - Country name.
     */
    SET_GEOLOCATION(state, geolocation) {
      state.geolocation = geolocation
    },

    /**
     * Set language.
     * @param {Object} language - Shopify localization.language.
     */
    SET_LANGUAGE(state, language) {
      state.language = language
    },

    /**
     * Set preference.
     * @param {Object} preference - User preference.
     * @param {String} preference.country - Country ISO code.
     * @param {String} preference.name - Country name.
     */
    SET_PREFERENCE(state, preference) {
      state.preference = preference
    },

    /**
     * Set selected store.
     * @param {String} selected - Selected store.
     */
    SET_SELECTED_STORE(state, selected) {
      state.selectedStore = selected
      const currentInstance = selected.instance.url
      location.href = currentInstance ? currentInstance : '/'
    },

    /**
     * Set suggested store.
     * @param {Object} suggestedStore - Suggested store.
     * @param {String} suggestedStore.country - Suggested country object.
     * @param {String} suggestedStore.language - Suggested language object.
     */
    SET_SUGGESTED_STORE(state, suggestedStore) {
      state.suggestedStore = suggestedStore
    },

    /**
     * Set supported.
     * @param {Object} supported - Supported countries.
     */
    SET_SUPPORTED(state, supported) {
      state.supported = supported
    },

    /**
     * Set trigger.
     * @param {String} trigger - Modal trigger.
     */
    SET_TRIGGER(state, trigger) {
      state.trigger = trigger
    },

    /**
     * Set template.
     * @param {String} trigger - Modal template.
     */
    SET_TEMPLATE(state, template) {
      state.template = template
    },
  },

  actions: {

    /**
     * Commit all language array.
     * @param {Object} languages - Language names array.
     * settings.
     */
    setAllLanguages({ commit }, languages) {
      commit('SET_ALL_LANGUAGES', languages)
    },

    /**
     * Commit continents object.
     * @param {Object} continents - Continents organisation from section
     * settings.
     */
    setContinents({ commit }, continents) {
      commit('SET_CONTINENTS', continents)
    },

    /**
     * Commit current country and language.
     * @param {Object} current - Current Shopify localization country and
     * language.
     */
    setCurrent({ commit }, current) {
      commit('SET_CURRENT', current)
    },

    /**
     * Commit geolocation.
     * @param {Object} response - Response from Shopify geolocation app.
     */
    setGeolocation({ commit }, response) {
      const { name, handle: country } = response.detected_values.country
      commit('SET_GEOLOCATION', { country, name })
    },

    /**
     * Commit preference.
     * @param {Object} preference - User preference.
     */
    setPreference({ commit }, preference) {
      commit('SET_PREFERENCE', preference)
    },

    /**
     * Commit selected store.
     * @param {String} selected - Selected store.
     */
    setSelectedStore({ commit }, selected) {
      commit('SET_SELECTED_STORE', selected)
    },

    /**
     * Commit suggested store.
     * @param {Object} suggestedStore - Suggested store.
     */
    setSuggestedStore({ commit }, suggestedStore) {
      commit('SET_SUGGESTED_STORE', suggestedStore)
    },

    /**
     * Commit supported.
     * @param {Object} supported - Supported countries.
     */
    setSupported({ commit }, supported) {
      commit('SET_SUPPORTED', supported)
    },

    /**
     * Commit trigger.
     * @param {String} trigger - Modal trigger.
     */
    setTrigger({ commit }, trigger) {
      commit('SET_TRIGGER', trigger)
    },

    /**
     * Commit template.
     * @param {String} template - Modal template.
     */
    setTemplate({ commit }, template) {
      commit('SET_TEMPLATE', template)
    },
  },
}
